import { useMemo, useState } from "react";
import AppShell from "@/components/AppShell";
import TeacherClassPicker from "@/components/TeacherClassPicker";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  EXAMS,
  classKey,
  getResultsForClass,
  getSchoolState,
  getSessionUser,
  getStudentsForClass,
  saveResultPdf,
} from "@/lib/eisStore";
import { decodeClassKey, isSupabaseConfigured, uploadDocument } from "@/lib/supabaseOps";
import { useSupabaseFileUrl } from "@/hooks/useSupabaseFileUrl";

function ResultFileLink({ examKey, payload }) {
  const payloadUrl = useSupabaseFileUrl(payload, "class-files");
  return (
    <a
      href={payloadUrl || payload?.dataUrl || "#"}
      download={payload?.fileName || "result.pdf"}
      className="block rounded-xl border border-slate-200 px-4 py-3 hover:bg-slate-50 transition"
    >
      <p className="font-semibold text-slate-800">
        {EXAMS.find((exam) => exam.key === examKey)?.label || examKey}
      </p>
      <p className="text-xs text-slate-500">{payload?.fileName}</p>
    </a>
  );
}

function ResultPreview({ payload, examLabel }) {
  const payloadUrl = useSupabaseFileUrl(payload, "class-files");
  if (!payloadUrl && !payload?.dataUrl) {
    return null;
  }

  return (
    <div data-testid="result-placeholder" className="rounded-xl border border-slate-200 p-4">
      <p className="text-sm text-slate-500 mb-2">Selected result</p>
      <a
        href={payloadUrl || payload?.dataUrl}
        download={payload?.fileName || "result.pdf"}
        className="inline-flex rounded-full px-4 py-2 text-white font-semibold"
        style={{ backgroundColor: "#0b1f7a" }}
      >
        Open {examLabel || "PDF"}
      </a>
    </div>
  );
}

function StudentResult({ user }) {
  const school = getSchoolState();
  const studentClassKey = classKey(user.standard, user.division);
  const classResults = getResultsForClass(studentClassKey);
  const examsWithFiles = Object.entries(classResults[user.id] || {});

  const [selectedExam, setSelectedExam] = useState("");

  return (
    <AppShell>
      <div data-testid="result-page" className="space-y-6">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold" style={{ color: "#0b1f7a" }}>
            Result
          </h2>
          <p className="text-slate-500 mt-1 text-sm">
            View the PDFs uploaded for your class.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[0.9fr_1.1fr] gap-6">
          <Card>
            <CardHeader>
              <CardTitle style={{ color: "#0b1f7a" }}>Available Exams</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Exam</Label>
                <Select value={selectedExam} onValueChange={setSelectedExam}>
                  <SelectTrigger className="mt-1" data-testid="result-exam-select">
                    <SelectValue placeholder="Choose exam..." />
                  </SelectTrigger>
                  <SelectContent>
                    {EXAMS.map((exam) => (
                      <SelectItem
                        key={exam.key}
                        value={exam.key}
                        data-testid={`result-option-${exam.key}`}
                      >
                        {exam.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                {examsWithFiles.length ? (
                  examsWithFiles.map(([examKey, payload]) => (
                    <ResultFileLink key={examKey} examKey={examKey} payload={payload} />
                  ))
                ) : (
                  <p className="text-sm text-slate-500">
                    No result PDF has been uploaded for your profile yet.
                  </p>
                )}
              </div>

              {selectedExam && school.results?.[studentClassKey]?.[user.id]?.[selectedExam] && (
                <ResultPreview
                  payload={school.results[studentClassKey][user.id][selectedExam]}
                  examLabel={EXAMS.find((exam) => exam.key === selectedExam)?.label}
                />
              )}
            </CardContent>
          </Card>

        </div>
      </div>
    </AppShell>
  );
}

function TeacherResult({ user }) {
  const [teacherClassKey, setTeacherClassKey] = useState(user.assignedClassKey || "");
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [selectedExam, setSelectedExam] = useState(EXAMS[0].key);
  const [uploading, setUploading] = useState(false);
  const students = useMemo(
    () => (teacherClassKey ? getStudentsForClass(teacherClassKey) : []),
    [teacherClassKey],
  );
  const classResults = teacherClassKey ? getResultsForClass(teacherClassKey) : {};

  const selectedStudent = students.find((student) => student.id === selectedStudentId) || students[0] || null;
  const selectedPayload = selectedStudent ? classResults?.[selectedStudent.id]?.[selectedExam] : null;
  const selectedPayloadUrl = useSupabaseFileUrl(selectedPayload, "class-files");

  const upload = async (file) => {
    if (!teacherClassKey) {
      toast.error("Please select a class first");
      return;
    }
    if (!selectedStudent) {
      toast.error("Please choose a student");
      return;
    }
    if (!file) {
      toast.error("Please choose a PDF");
      return;
    }

    setUploading(true);
    try {
      const { standard, division } = decodeClassKey(teacherClassKey);
      const examLabel = EXAMS.find((exam) => exam.key === selectedExam)?.label || selectedExam;
      let filePath = "";
      if (isSupabaseConfigured) {
        try {
          const result = await uploadDocument(file, standard, division, "result", `${selectedStudent.name} - ${examLabel}`);
          filePath = result?.path || "";
        } catch (error) {
          console.error(error);
        }
      }
      saveResultPdf(teacherClassKey, selectedStudent.id, selectedExam, {
        fileName: file.name,
        filePath: filePath || undefined,
        bucket: "class-files",
        uploadedAt: new Date().toISOString(),
      });
      toast.success("Result PDF uploaded");
    } finally {
      setUploading(false);
    }
  };

  return (
    <AppShell>
      <div data-testid="result-page" className="space-y-6">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold" style={{ color: "#0b1f7a" }}>
            Result
          </h2>
          <p className="text-slate-500 mt-1 text-sm">
            Upload and manage result PDFs for your class.
          </p>
        </div>

        <TeacherClassPicker
          teacher={user}
          onChange={(value) => setTeacherClassKey(value)}
        />

        <div className="grid grid-cols-1 lg:grid-cols-[0.95fr_1.05fr] gap-6">
          <Card>
            <CardHeader>
              <CardTitle style={{ color: "#0b1f7a" }}>Student List</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {!teacherClassKey ? (
                <p className="text-sm text-slate-500">
                  Select a class to load the student list.
                </p>
              ) : (
                students.map((student) => {
                  const uploaded = classResults?.[student.id] || {};
                  return (
                    <button
                      key={student.id}
                      type="button"
                      onClick={() => setSelectedStudentId(student.id)}
                      className={`w-full text-left rounded-xl border px-4 py-3 transition ${
                        selectedStudent?.id === student.id
                          ? "border-[#0b1f7a] bg-[#EEF0FA]"
                          : "border-slate-200 hover:bg-slate-50"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="font-semibold text-slate-800">{student.name}</p>
                          <p className="text-xs text-slate-500">
                            Roll {student.rollNo} · {student.standard} {student.division}
                          </p>
                        </div>
                        <div className="flex flex-wrap gap-1 justify-end">
                          {Object.keys(uploaded).length ? (
                            Object.keys(uploaded).map((examKey) => (
                              <span
                                key={examKey}
                                className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700"
                              >
                                {EXAMS.find((exam) => exam.key === examKey)?.label || examKey}
                              </span>
                            ))
                          ) : (
                            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
                              No PDF
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle style={{ color: "#0b1f7a" }}>Upload Result PDF</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Selected Student</Label>
                <p className="mt-1 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
                  {selectedStudent ? selectedStudent.name : "Choose a student from the list"}
                </p>
              </div>

              <div>
                <Label>Exam</Label>
                <Select value={selectedExam} onValueChange={setSelectedExam}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Choose exam" />
                  </SelectTrigger>
                  <SelectContent>
                    {EXAMS.map((exam) => (
                      <SelectItem key={exam.key} value={exam.key}>
                        {exam.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Upload PDF</Label>
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={(e) => upload(e.target.files?.[0])}
                  className="mt-1 block w-full text-sm text-slate-500 file:mr-4 file:rounded-full file:border-0 file:bg-[#0b1f7a] file:px-4 file:py-2 file:text-white hover:file:opacity-90"
                />
              </div>

              {selectedStudent && selectedPayload && (
                <div className="rounded-xl border border-slate-200 p-4">
                  <p className="text-sm font-semibold text-slate-800">
                    Uploaded PDF exists for {selectedStudent.name}
                  </p>
                  <a
                    href={selectedPayloadUrl || selectedPayload.dataUrl || "#"}
                    download={selectedPayload.fileName || "result.pdf"}
                    className="mt-3 inline-flex rounded-full px-4 py-2 text-white font-semibold"
                    style={{ backgroundColor: "#0b1f7a" }}
                  >
                    Open PDF
                  </a>
                </div>
              )}

              {uploading && <p className="text-sm text-slate-500">Uploading...</p>}
            </CardContent>
          </Card>
        </div>
      </div>
    </AppShell>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-xl bg-slate-50 border border-slate-200 px-4 py-3">
      <span className="text-sm text-slate-500">{label}</span>
      <span className="text-sm font-semibold text-slate-800">{value}</span>
    </div>
  );
}

export default function Result() {
  const user = getSessionUser();
  if (user?.role === "teacher") {
    return <TeacherResult user={user} />;
  }
  return <StudentResult user={user || {}} />;
}
